import React from 'react';

import { cssMap as unboundCssMap } from '@compiled/react';

import { cssMap, cx } from '@atlaskit/css';
import Link from '@atlaskit/link';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import {
	ActionName,
	CustomUnresolvedAction,
	ElementName,
	FooterBlock,
	MetadataBlock,
	SmartLinkSize,
	SnippetBlock,
	TitleBlock,
} from '@atlaskit/smart-card';
import { token } from '@atlaskit/tokens';

import { type NLPSearchResultSource } from '../../../ai-answer-dialog/types';
import { logger } from '../../../common/utils/logger';
import { extractUrlHostname } from '../../../utils';
import { AppIcon } from '../app-icon/app-icon';
import { ErrorBoundary } from '../error-boundary';

const Card = React.lazy(() =>
	import(/* webpackChunkName: "@atlaskit-internal_.smart-card" */ '@atlaskit/smart-card').then(
		({ Card }) => ({
			default: Card,
		}),
	),
);

const resolveUrlPlaceholder = (url: string) => {
	const textFragmentRegex = new RegExp('#:~:text=.*$', 'g');
	const urlWithoutTextFragment = url?.replace(textFragmentRegex, '');
	const maxLength = 30;
	const truncatedUrl =
		urlWithoutTextFragment.length > maxLength
			? urlWithoutTextFragment.slice(0, maxLength) + '...'
			: urlWithoutTextFragment;
	return truncatedUrl;
};

const constructSourceUrl = ({
	source,
	searchSessionId,
}: {
	source: NLPSearchResultSource;
	searchSessionId: string;
}) => {
	if (source.product === 'Confluence') {
		const url = new URL(source.url);
		url.searchParams.append('search_id', searchSessionId);
		return url.toString();
	} else {
		return source.url;
	}
};

const fallbackComponent =
	({
		index,
		source,
		searchSessionId,
		onClickHandler,
	}: Omit<SourceCardProps, 'enableSmartLinkSources'>) =>
	() => {
		return fg('dst-a11y__replace-anchor-with-link__knowledge-disc') ? (
			<Link
				href={constructSourceUrl({ source, searchSessionId })}
				onClick={(e) => onClickHandler(index, source, extractUrlHostname(source.url))}
				rel="noopener noreferrer"
				target="_blank"
			>
				{source.title ?? source.url}
			</Link>
		) : (
			// eslint-disable-next-line @atlaskit/design-system/no-html-anchor
			<a
				href={constructSourceUrl({ source, searchSessionId })}
				onClick={(e) => onClickHandler(index, source, extractUrlHostname(source.url))}
				rel="noopener noreferrer"
				target="_blank"
			>
				{source.title ?? source.url}
			</a>
		);
	};

type SourceCardProps = {
	index: number;
	source: NLPSearchResultSource;
	searchSessionId: string;
	onClickHandler: (index: number, source: NLPSearchResultSource, hostname?: string) => void;
	enableSmartLinkSources: boolean;
};

export const SourceCard = ({
	index,
	source,
	searchSessionId,
	onClickHandler,
	enableSmartLinkSources,
}: SourceCardProps) => {
	const SmartLinkSource = (
		<Card
			appearance="inline"
			url={source.url}
			removeTextHighlightingFromTitle
			showHoverPreview
			fallbackComponent={fallbackComponent({
				index,
				source,
				searchSessionId,
				onClickHandler,
			})}
			onClick={(e) => {
				onClickHandler(index, source, extractUrlHostname(source.url));
				e.preventDefault();
				window.open(
					constructSourceUrl({ source, searchSessionId }),
					'_blank',
					'noopener, noreferrer',
				);
			}}
			onError={() => {
				// Nothing for now
			}}
			resolvingPlaceholder={resolveUrlPlaceholder(source.url)}
			placeholder={resolveUrlPlaceholder(source.url)}
		/>
	);

	return (
		<ErrorBoundary fallback={<></>}>
			{enableSmartLinkSources
				? SmartLinkSource
				: fallbackComponent({ index, source, searchSessionId, onClickHandler })()}
		</ErrorBoundary>
	);
};

const styles = cssMap({
	root: {
		borderColor: token('color.border'),
		borderStyle: 'solid',
		borderRadius: token('radius.xlarge'),
		borderWidth: token('border.width'),
		breakInside: 'avoid',
		display: 'flex',
		height: '174px',
		justifyContent: 'stretch',
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
		paddingTop: token('space.200'),
	},
	unresolvedActionContainerOuter: {
		display: 'flex',
		justifyContent: 'end', // Push inner container to the right
		marginTop: 'auto', // Push outer container to the bottom
	},
	unresolvedActionContainerInner: {
		alignItems: 'center',
		backgroundColor: token('color.background.neutral.subtle'),
		borderColor: token('color.border'),
		borderRadius: token('radius.small'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		color: token('color.text.subtle'),
		display: 'inline-flex',
		font: token('font.body'),
		fontWeight: token('font.weight.medium'),
		justifyContent: 'center',
		minHeight: '24px',
		paddingBottom: token('space.0'),
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
		paddingTop: token('space.0'),
		textAlign: 'center',
	},
	footerContainer: {
		marginTop: 'auto',
	},
});

const unboundStyles = unboundCssMap({
	root: {
		// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors
		'.loader-wrapper': {
			display: 'flex',
			justifyContent: 'stretch',
		},
	},
});

function sourceFlexibleSmartCardFallbackComponent({
	index,
	source,
	searchSessionId,
	onClickHandler,
}: SourceFlexibleSmartCardProps) {
	return () => (
		<Link
			href={constructSourceUrl({ source, searchSessionId })}
			onClick={(e) => onClickHandler(index, source, extractUrlHostname(source.url))}
			rel="noopener noreferrer"
			target="_blank"
		>
			{source.title ?? source.url}
		</Link>
	);
}

function CustomUnresolvedActionContainer({ children }: { children: React.ReactNode }) {
	return (
		<Box xcss={styles.unresolvedActionContainerOuter}>
			<Box xcss={styles.unresolvedActionContainerInner}>{children}</Box>
		</Box>
	);
}

type SourceFlexibleSmartCardProps = {
	index: number;
	onClickHandler: (index: number, source: NLPSearchResultSource, hostname?: string) => void;
	searchSessionId: string;
	source: NLPSearchResultSource;
};

export function SourceFlexibleSmartCard({
	index,
	source,
	searchSessionId,
	onClickHandler,
}: SourceFlexibleSmartCardProps) {
	return (
		<ErrorBoundary fallback={<></>}>
			<Box xcss={cx(styles.root, unboundStyles.root)}>
				<Card
					appearance="block"
					ui={{
						clickableContainer: true,
						hideBackground: true,
						hideElevation: true,
						hidePadding: true,
						removeBlockRestriction: true,
					}}
					url={source.url}
					fallbackComponent={sourceFlexibleSmartCardFallbackComponent({
						index,
						source,
						searchSessionId,
						onClickHandler,
					})}
					onClick={(e) => {
						onClickHandler(index, source, extractUrlHostname(source.url));
						e.preventDefault();
						window.open(
							constructSourceUrl({ source, searchSessionId }),
							'_blank',
							'noopener, noreferrer',
						);
					}}
					onError={() => {
						logger.error({
							error: new Error('Error rendering source card'),
							functionName: 'SourceFlexibleSmartCard',
						});
					}}
					resolvingPlaceholder={resolveUrlPlaceholder(source.url)}
					placeholder={resolveUrlPlaceholder(source.url)}
				>
					<Inline alignBlock="center" space="space.100">
						<AppIcon app={source.product} size="small" />
						<TitleBlock hideIcon hideRetry />
					</Inline>
					<MetadataBlock
						maxLines={1}
						primary={[
							{ name: ElementName.AuthorGroup },
							{ name: ElementName.ModifiedOn },
							{ name: ElementName.SentOn },
						]}
					/>
					<SnippetBlock maxLines={2} />
					<Box xcss={styles.footerContainer}>
						<FooterBlock
							actions={[
								{ name: ActionName.PreviewAction, hideIcon: true, size: SmartLinkSize.Small },
								{ name: ActionName.CopyLinkAction, hideIcon: true, size: SmartLinkSize.Small },
							]}
							hideProvider
						/>
					</Box>
					{/* CustomUnresolvedAction only displays when the URL isn't resolved */}
					<CustomUnresolvedAction Container={CustomUnresolvedActionContainer} />
				</Card>
			</Box>
		</ErrorBoundary>
	);
}
